import { useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { CircularProgress, FormControlLabel, makeStyles, Radio, RadioGroup } from '@material-ui/core';
import _, { isEmpty } from 'lodash';
import clsx from 'clsx';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import { AUTO_COMPLETE_DEFAULT, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpTextField from '../../form/NgpTextField';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import IPAddressUtilities from '../../../utilities/IPAddressUtilities';
import {
    DESCRIPTION_FIELD,
    DISCOVER_FIELD,
    EXCLUDE_FIELD,
    SCOPE_TYPE,
    SUBNET_FIELD,
    VALUE_FIELD,
    VALUE_SUBNET_FIELD,
} from './CollectorIpFormConstants';
import NgpCheckbox from '../../form/NgpCheckbox';
import { useDataService } from '../../../services/ApiDataService';
import useAlerts from '../../../stores/AlertsStore';
import { buildLinkUrl } from '../../links/Link';

const formId = 'collectFrmIpScope';
const addButtonText = t`Ok`;
const updateButtonText = t`Update`;
const defaultFormValues = {
    [EXCLUDE_FIELD]: false,
    [DISCOVER_FIELD]: true,
    scopeType: SCOPE_TYPE.monitor,
    [VALUE_FIELD]: { [SUBNET_FIELD]: '' },
};

const MIN_SUBNET_MASK_DISCOVERY = 16;
const MIN_SUBNET_MASK_NO_DISCOVERY = 0;

export const useStyles = makeStyles({
    root: {
        padding: 0,
        margin: 0,
        display: 'flex',
    },
    radioGroup: {
        justifyContent: 'flex-end',
    },
    checkBox: {
        marginLeft: '30px',
    },
});

export default function CollectorEditIpScopeForm({
    associatedCollector,
    setSubnetRangeCache,
    selectedRecord,
    selectedList = [],
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
}) {
    const classes = useStyles();
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: defaultFormValues,
    });
    const internalId = useRef(null);
    const buttonText = useRef(addButtonText);
    const dataService = useDataService();
    const [, alertsActions] = useAlerts();
    const { handleSubmit, formState, reset, getValues, setValue, trigger } = methods;
    const [saving, setSaving] = useState(false);
    const [scopeType, setScopeType] = useState(SCOPE_TYPE.monitor);
    const [exclude, setExclude] = useState(false);
    const isMounted = useRef(false);

    const existingScopes = _.cloneDeep(selectedList);
    _.remove(existingScopes, (sc) => _.isEqual(sc, selectedRecord));

    useEffect(() => {
        if (!open) {
            return;
        }

        if (_.isEmpty(selectedRecord)) {
            internalId.current = null;
            buttonText.current = addButtonText;
            setScopeType(SCOPE_TYPE.monitor);
            setExclude(defaultFormValues[EXCLUDE_FIELD]);
            reset(defaultFormValues);
            return;
        }

        internalId.current = selectedRecord[INTERNAL_ID_FIELD];
        buttonText.current = selectedRecord[INTERNAL_ID_FIELD] ? updateButtonText : addButtonText;

        const type = selectedRecord[EXCLUDE_FIELD] ? SCOPE_TYPE.exclude : SCOPE_TYPE.monitor;
        setScopeType(type);

        setExclude(selectedRecord[EXCLUDE_FIELD]);

        const scope = {
            ...selectedRecord,
            scopeType: type,
        };

        reset(scope);
    }, [open, selectedRecord, reset]);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        trigger(VALUE_SUBNET_FIELD);
    }, [exclude, trigger]);

    const handleCleanup = () => {
        internalId.current = null;
        buttonText.current = addButtonText;
        setSelectedRecord(undefined);
    };

    const handleFormSubmit = async (dataToSave = {}) => {
        if (internalId) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        dataToSave.exclude = exclude;
        dataToSave.discover = scopeType === SCOPE_TYPE.exclude ? false : !!dataToSave[DISCOVER_FIELD];

        setSaving(true);

        try {
            await onConfirm(dataToSave);
            const subnetRangeCache = {};
            const currentSubnet = dataToSave[VALUE_FIELD][SUBNET_FIELD];
            subnetRangeCache[currentSubnet] = IPAddressUtilities.getRangeBigIntegers(currentSubnet);
            setSubnetRangeCache(subnetRangeCache);
            handleCleanup();
        } finally {
            setSaving(false);
        }
    };

    const handleScopeTypeChange = (e) => {
        const newScopeType = e.target.value;
        setScopeType(newScopeType);
        const wasExcluded = exclude;
        const newExclude = newScopeType === SCOPE_TYPE.exclude;
        let newDiscover = getValues(DISCOVER_FIELD);

        // Only update the discovery flag if the exclusion flag changed.
        if (wasExcluded !== newExclude) {
            newDiscover = newScopeType !== SCOPE_TYPE.exclude;
        }

        setExclude(newExclude);
        setValue(DISCOVER_FIELD, newDiscover);
    };

    const ipRules = {
        validate: (value) => ipValidations(value),
    };

    const ipValidations = async (value) => {
        if (!validateIP(value)) return i18n._(t`Please enter a valid IPv4 subnet`);
        if (!largeSubnet(value)) return i18n._(t`Subnet mask size cannot exceed /16 for discovery enabled scopes`);
        if (!uniqueSubnet(value)) return i18n._(t`This subnet has already been added to this collector\'s IP scopes`);

        const result = await overlaps(value);
        if (!_.isEmpty(result)) return i18n._(t`This subnet overlaps with the following collector(s): ${result}`);
    };

    const validateIP = (value) => {
        const rangeSeparator = '-';
        const ips = _.map(value.split(rangeSeparator), _.trim);
        const [startIp, endIp] = ips;

        if (ips.length === 2) {
            // this is an IP range. E.g., 192.168.0.0-192.168.0.2
            const v4Addresses = IPAddressUtilities.isIPv4Address(startIp) && IPAddressUtilities.isIPv4Address(endIp);
            const isOrdered = IPAddressUtilities.compareTo(startIp, endIp) === -1;

            return v4Addresses && isOrdered;
        }

        if (ips.length === 1) {
            // normal IP or subnet
            return IPAddressUtilities.isV4(startIp);
        }

        // this is an invalid range, return false.
        return false;
    };

    const largeSubnet = (value) => {
        return getValues(DISCOVER_FIELD)
            ? IPAddressUtilities.getSubnetMask(value) >= MIN_SUBNET_MASK_DISCOVERY
            : IPAddressUtilities.getSubnetMask(value) >= MIN_SUBNET_MASK_NO_DISCOVERY;
    };

    const uniqueSubnet = (value) => {
        return _.isUndefined(
            _.find(
                existingScopes,
                (scope) =>
                    scope.value.subnet === value &&
                    (scope._id !== selectedRecord?._id || selectedRecord?._id === undefined)
            )
        );
    };

    const overlaps = async (value) => {
        const overlappingUrl = '/ngp/v2/inventory/collectors/:_id/overlapping';
        // Make sure the value is a valid subnet and is not an exclude scope before checking for overlaps.
        const collectorId = associatedCollector._id;
        let matchingCollectors;

        if (IPAddressUtilities.isSubnet(value) && !exclude) {
            if (!isEmpty(value)) {
                const query = { subnet: value };
                if (collectorId) {
                    const url = buildLinkUrl({ target: overlappingUrl }, { _id: collectorId }, null);
                    await dataService
                        .get(url, query)
                        .then((response) => {
                            matchingCollectors = response.data;
                            // grab the collector names which are overlapping
                            if (!matchingCollectors || !_.isArray(matchingCollectors)) {
                                return true;
                            }
                        })
                        .catch((e) => {
                            if (e?.responseBody?.error) {
                                alertsActions.createErrorAlert(e.responseBody.error);
                            }
                        });
                    if (exclude) {
                        return '';
                    }
                    return _.map(matchingCollectors, 'name').join(', ');
                }
            }
        } else {
            return '';
        }
    };

    const startIcon = saving && <CircularProgress color="inherit" variant="indeterminate" size={16} />;

    return (
        <NgpTwoButtonDialog
            title={<Trans>Collector IP Scope</Trans>}
            confirmButtonText={buttonText.current}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid || saving}
            confirmButtonProps={{ startIcon }}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={formId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <FormSection>
                        <NgpTextField
                            fieldName={VALUE_SUBNET_FIELD}
                            label={<Trans>Subnet</Trans>}
                            required
                            fieldRules={ipRules}
                            inputProps={{
                                size: 65,
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />

                        <NgpTextField
                            fieldName={DESCRIPTION_FIELD}
                            label={<Trans>Description</Trans>}
                            inputProps={{
                                size: 65,
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />
                    </FormSection>
                    <RadioGroup
                        className={clsx(classes.radioGroup)}
                        aria-label="type-of-scopeType"
                        name="type-of-scopeType"
                        value={scopeType}
                        onChange={handleScopeTypeChange}
                    >
                        <FormControlLabel
                            value={SCOPE_TYPE.monitor}
                            control={<Radio />}
                            label={<Trans>Monitor devices in this scope</Trans>}
                        />
                        <div className={clsx(classes.checkBox)}>
                            <NgpCheckbox
                                fieldName={DISCOVER_FIELD}
                                label={<Trans>Enable discovery in this scope</Trans>}
                                disabled={scopeType === SCOPE_TYPE.exclude}
                            />
                        </div>
                        <FormControlLabel
                            value={SCOPE_TYPE.exclude}
                            control={<Radio />}
                            label={<Trans>Exclude devices in this scope from discovery</Trans>}
                        />
                    </RadioGroup>
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
